import { useState } from "react";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { QrCode } from "lucide-react";
import { QrCodeRenderer } from "./components/qr-code-renderer.component";
import { QrCodeRawRenderer } from "./components/qr-code-raw-renderer.component";
import { PaymentSlipDataInput } from "../../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { BankAccountEntityType, ClientEntityType } from "@/lib/supabase/supabaseTypes";
import { Currency } from "../../../types/enums";

const defaultBankAccount: BankAccountEntityType = {
	id: 1,
	organization_id: "test-org",
	qr_iban: "CH4431999123000889012",
	reference_number: "123456789012",
	cdtr_name: "Test Labor AG",
	cdtr_adrtp: "S",
	cdtr_street_line_1: "Teststrasse",
	cdtr_house_number_line_2: "123",
	cdtr_area_code: "8000",
	cdtr_city: "Zürich",
	cdtr_country: "CH",
	created_at: new Date().toISOString(),
	modified_at: new Date().toISOString(),
	account_name: "Test Labor AG",
	bank_city: "Zürich",
	bank_name: "Test Labor AG",
	currency: "CHF",
	is_pc_account: false,
	letterhead: "Test Labor AG",
	pc_account: "123456789012",
};

const defaultRecipient: ClientEntityType = {
	id: "test-client",
	organization_id: "test-org",
	first_name: "Max",
	last_name: "Mustermann",
	street: "Musterstrasse",
	postal_code: "8001",
	city: "Zürich",
	country: "CH",
	is_company: false,
	is_correspondence_address: false,
	created_at: new Date().toISOString(),
	modified_at: new Date().toISOString(),
	phone_personal: "0123456789",
	phone_business: "0987654321",
	fax: "0123456789",
	favorite: null,
	phone_mobile: "0771234567",
	email: "test@test.com"
} as ClientEntityType;

const defaultRawString = `SPC
0200
1
CH4431999123000889012
S
Test Labor AG
Teststrasse
123
8000
Zürich
CH




100.00
CHF
S
Max Mustermann

8001
Zürich
CH
QRR
000000000000000000000000000
Test Zahlung
EPD
`;

type InputMode = "form" | "raw";

export const QrTesterPage = () => {
	const [mode, setMode] = useState<InputMode>("form");
	const [paymentData, setPaymentData] = useState<PaymentSlipDataInput>({
		bankAccount: defaultBankAccount,
		recipient: defaultRecipient,
		amount: "100.00",
		currency: Currency.CHF,
		invoiceNumber: "123456",
		unstructuredMessage: "Test Zahlung",
	});
	const [rawString, setRawString] = useState<string>(defaultRawString);

	const handleFieldChange = (field: string, value: string) => {
		setPaymentData((prev) => {
			if (field.startsWith("bankAccount.")) {
				const bankField = field.replace("bankAccount.", "");
				return {
					...prev,
					bankAccount: {
						...prev.bankAccount,
						[bankField]: value,
					},
				};
			}
			if (field.startsWith("recipient.")) {
				const recipientField = field.replace("recipient.", "");
				return {
					...prev,
					recipient: {
						...prev.recipient,
						[recipientField]: value,
					},
				};
			}
			return {
				...prev,
				[field]: value,
			};
		});
	};

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[{ name: "Support" }, { name: "QR-Code Tester" }]}
					icon={<QrCode width="16px" height="16px" color="rgb(55, 65, 81)" />}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				<div className="flex flex-col gap-2">
					<h1 className="text-2xl font-bold text-gray-900">
						QR-Code Tester
					</h1>
					<p className="text-gray-600">
						Testen Sie die QR-Code Generierung mit benutzerdefinierten
						Zahlschein-Daten. Fehler werden automatisch abgefangen und
						angezeigt.
					</p>
				</div>

				<div className="bg-white border border-gray-200 rounded-lg p-4">
					<div className="flex gap-2">
						<button
							onClick={() => setMode("form")}
							className={`px-4 py-2 rounded-md font-medium transition-colors ${
								mode === "form"
									? "bg-blue-600 text-white"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200"
							}`}
							data-testid="mode-form-button"
							aria-label="Formular Modus"
						>
							Formular
						</button>
						<button
							onClick={() => setMode("raw")}
							className={`px-4 py-2 rounded-md font-medium transition-colors ${
								mode === "raw"
									? "bg-blue-600 text-white"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200"
							}`}
							data-testid="mode-raw-button"
							aria-label="Raw String Modus"
						>
							Raw String
						</button>
					</div>
				</div>

				{mode === "form" ? (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<div className="flex flex-col gap-4">
							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<h3 className="text-lg font-semibold mb-4">
									Zahlungsinformationen
								</h3>

								<div className="space-y-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Betrag
										</label>
										<input
											type="text"
											value={paymentData.amount}
											onChange={(e) =>
												handleFieldChange("amount", e.target.value)
											}
											className="w-full px-3 py-2 border border-gray-300 rounded-md"
											placeholder="100.00"
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Währung
										</label>
										<select
											value={paymentData.currency}
											onChange={(e) =>
												handleFieldChange("currency", e.target.value)
											}
											className="w-full px-3 py-2 border border-gray-300 rounded-md"
										>
											<option value={Currency.CHF}>CHF</option>
											<option value={Currency.EUR}>EUR</option>
										</select>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Rechnungsnummer (6-stellig)
										</label>
										<input
											type="text"
											value={paymentData.invoiceNumber}
											onChange={(e) =>
												handleFieldChange("invoiceNumber", e.target.value)
											}
											className="w-full px-3 py-2 border border-gray-300 rounded-md"
											placeholder="123456"
											maxLength={6}
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Mitteilung (optional)
										</label>
										<input
											type="text"
											value={paymentData.unstructuredMessage || ""}
											onChange={(e) =>
												handleFieldChange("unstructuredMessage", e.target.value)
											}
											className="w-full px-3 py-2 border border-gray-300 rounded-md"
											placeholder="Zahlungsmitteilung"
										/>
									</div>
								</div>
							</div>

							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<h3 className="text-lg font-semibold mb-4">Bankkonto</h3>

								<div className="space-y-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											QR-IBAN
										</label>
										<input
											type="text"
											value={paymentData.bankAccount.qr_iban || ""}
											onChange={(e) =>
												handleFieldChange("bankAccount.qr_iban", e.target.value)
											}
											className="w-full px-3 py-2 border border-gray-300 rounded-md"
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Referenznummer
										</label>
										<input
											type="text"
											value={paymentData.bankAccount.reference_number || ""}
											onChange={(e) =>
												handleFieldChange("bankAccount.reference_number", e.target.value)
											}
											className="w-full px-3 py-2 border border-gray-300 rounded-md"
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Name
										</label>
										<input
											type="text"
											value={paymentData.bankAccount.cdtr_name || ""}
											onChange={(e) =>
												handleFieldChange("bankAccount.cdtr_name", e.target.value)
											}
											className="w-full px-3 py-2 border border-gray-300 rounded-md"
										/>
									</div>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Strasse
											</label>
											<input
												type="text"
												value={paymentData.bankAccount.cdtr_street_line_1 || ""}
												onChange={(e) =>
													handleFieldChange("bankAccount.cdtr_street_line_1", e.target.value)
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Hausnummer
											</label>
											<input
												type="text"
												value={paymentData.bankAccount.cdtr_house_number_line_2 || ""}
												onChange={(e) =>
													handleFieldChange("bankAccount.cdtr_house_number_line_2", e.target.value)
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
									</div>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												PLZ
											</label>
											<input
												type="text"
												value={paymentData.bankAccount.cdtr_area_code || ""}
												onChange={(e) =>
													handleFieldChange("bankAccount.cdtr_area_code", e.target.value)
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Stadt
											</label>
											<input
												type="text"
												value={paymentData.bankAccount.cdtr_city || ""}
												onChange={(e) =>
													handleFieldChange("bankAccount.cdtr_city", e.target.value)
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
									</div>
								</div>
							</div>

							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<h3 className="text-lg font-semibold mb-4">Empfänger</h3>

								<div className="space-y-4">
									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Vorname
											</label>
											<input
												type="text"
												value={paymentData.recipient.first_name || ""}
												onChange={(e) =>
													handleFieldChange("recipient.first_name", e.target.value)
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Nachname
											</label>
											<input
												type="text"
												value={paymentData.recipient.last_name || ""}
												onChange={(e) =>
													handleFieldChange("recipient.last_name", e.target.value)
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Strasse
										</label>
										<input
											type="text"
											value={paymentData.recipient.street || ""}
											onChange={(e) =>
												handleFieldChange("recipient.street", e.target.value)
											}
											className="w-full px-3 py-2 border border-gray-300 rounded-md"
										/>
									</div>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												PLZ
											</label>
											<input
												type="text"
												value={paymentData.recipient.postal_code || ""}
												onChange={(e) =>
													handleFieldChange("recipient.postal_code", e.target.value)
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Stadt
											</label>
											<input
												type="text"
												value={paymentData.recipient.city || ""}
												onChange={(e) =>
													handleFieldChange("recipient.city", e.target.value)
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="flex flex-col gap-4">
							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<h3 className="text-lg font-semibold mb-4">
									Generierter QR-Code
								</h3>
								<QrCodeRenderer paymentSlipData={paymentData} />
							</div>
						</div>
					</div>
				) : (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<div className="flex flex-col gap-4">
							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<h3 className="text-lg font-semibold mb-4">
									QR-Code String
								</h3>
								<p className="text-sm text-gray-600 mb-4">
									Geben Sie den vollständigen QR-Code String ein, der direkt
									an convertToUtf8() übergeben wird.
								</p>
								<textarea
									value={rawString}
									onChange={(e) => setRawString(e.target.value)}
									className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
									placeholder="SPC
0200
1
CH4431999123000889012
..."
									data-testid="raw-string-input"
									aria-label="QR-Code String Eingabe"
								/>
								<p className="text-xs text-gray-500 mt-2">
									Jede Zeile entspricht einem Feld im QR-Code Format. Siehe{" "}
									<a
										href="https://www.six-group.com/dam/download/banking-services/standardization/qr-bill/ig-qr-bill-v2.2-de.pdf"
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-600 hover:text-blue-800 underline"
									>
										Swiss QR Bill Dokumentation
									</a>
								</p>
							</div>
						</div>

						<div className="flex flex-col gap-4">
							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<h3 className="text-lg font-semibold mb-4">
									Generierter QR-Code
								</h3>
								<QrCodeRawRenderer rawString={rawString} />
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
